/**
 * Well Testing AI drafts (WT-6, fieldiq-api#1261): names, skins, who may press
 * what, and how a refusal reads. PF-6's lib/pf-ai.ts, for this vertical.
 *
 * NOTHING HERE COMPUTES A FIGURE. Hours, counts, flags and states are the
 * API's deterministic layer; this file words them. The AI's part is the draft
 * text only, shown as a draft until a person approves it.
 */
import { WT_UNKNOWN_SKIN, wtApiError, wtErrorMessage, type WtSkin } from './well-testing';
import type { WtAiAgent } from '../types/wt-ai';

/** Mirrors fieldiq-api lib/wt-roles.ts WT_AI_ROLES. For hiding, not enforcing. */
export const WT_AI_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const canUseWtAi = (role: string | null | undefined): boolean => !!role && (WT_AI_ROLES as readonly string[]).includes(role);

export const WT_AI_AGENT_LABEL: Record<WtAiAgent, string> = {
  wt_shift_summary: 'Shift summary',
  wt_ticket_preflight: 'Preflight',
  wt_test_report_draft: 'Test report draft',
  wt_compliance_digest: 'Compliance digest',
};
export const wtAiAgentLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(WT_AI_AGENT_LABEL, v) ? WT_AI_AGENT_LABEL[v as WtAiAgent] : '—';

const pick = (map: Record<string, WtSkin>, v: string | null | undefined): WtSkin =>
  v && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : WT_UNKNOWN_SKIN;

/**
 * Review outcome (agent_runs.outcome). A pending draft reads as WAITING ON A
 * PERSON, never as anything published. Unknown → `—`, never a guessed state.
 */
export const WT_AI_OUTCOME_SKIN: Record<string, WtSkin> = {
  pending: { label: 'Awaiting your approval', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  actioned: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  dismissed: { label: 'Dismissed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  superseded: { label: 'Superseded', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  no_action: { label: 'No action', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wtAiOutcomeSkin = (v: string | null | undefined): WtSkin => pick(WT_AI_OUTCOME_SKIN, v);

/** A compliance item's state (lib/wt-ai-digests.ts expiryState, missing, and the calibration day limit). */
export const WT_AI_STATE_SKIN: Record<string, WtSkin> = {
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  missing: { label: 'Missing', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  day_limit_reached: { label: 'Day limit reached', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
};
export const wtAiStateSkin = (v: string | null | undefined): WtSkin => pick(WT_AI_STATE_SKIN, v);

export const WT_SHIFT_LABEL: Record<string, string> = { day: 'Day shift', night: 'Night shift' };
export const wtShiftLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(WT_SHIFT_LABEL, v) ? WT_SHIFT_LABEL[v] : '—';

/** What a stored run covered, from its trigger data. Unknown → `—`. */
export function wtAiScopeText(agent: string, triggerData: Record<string, unknown> | null, day: (v: string | null) => string): string {
  const t = triggerData ?? {};
  const str = (k: string) => (typeof t[k] === 'string' && t[k] ? (t[k] as string) : null);
  if (agent === 'wt_shift_summary') {
    const parts = [str('jobNumber'), str('workDate') ? day(str('workDate')) : null, str('shift') ? wtShiftLabel(str('shift')).toLowerCase() : null]
      .filter((p): p is string => !!p && p !== '—');
    return parts.length ? parts.join(' · ') : '—';
  }
  if (agent === 'wt_ticket_preflight') return str('ticketNumber') ?? (str('jobNumber') ? `${str('jobNumber')} (whole job)` : '—');
  if (agent === 'wt_test_report_draft') return str('jobNumber') ? `Report on ${str('jobNumber')}` : '—';
  if (agent === 'wt_compliance_digest') return str('today') ? `As of ${day(str('today'))}` : '—';
  return '—';
}

/**
 * The statuses a ticket preflight may be offered on: draft, office approved,
 * customer rejected. PF's pfTicketPreflightable offers it on every ticket the
 * customer has not approved; WT means the same and adds `void`, because the WT
 * API refuses both (lib/wt-ai-digests.ts computeWtPreflight: 409
 * ticket_customer_approved, 409 ticket_void). An ALLOWLIST, so a status this
 * file does not know is not offered a run the API might refuse.
 */
export const WT_PREFLIGHTABLE_STATUSES = ['draft', 'office_approved', 'customer_rejected'] as const;
export const wtTicketPreflightable = (status: string | null | undefined): boolean =>
  !!status && (WT_PREFLIGHTABLE_STATUSES as readonly string[]).includes(status);

/** The API's edit limits on approve (routes/well-testing-ai.routes.ts). Checked there; mirrored here to say so first. */
export const WT_AI_MAX_NARRATIVE = 2000;
export const WT_AI_MAX_OBSERVATIONS = 8;
export const WT_AI_MAX_OBSERVATION = 400;

/**
 * A refusal from the AI routes, in the API's own words. These routes answer
 * `{ error, code }` and every `error` is already a sentence the office can
 * read, so it is shown as sent. A 403 is a refusal whatever the body says.
 */
export function wtAiRefusal(err: unknown, fallback: string): string {
  const e = wtApiError(err);
  if (e.status === 403) return 'You do not have permission to do this. AI drafts are for admins, office staff and supervisors.';
  return wtErrorMessage(err, fallback);
}
